import { useState, useEffect, useCallback } from 'react'

type Theme = 'light' | 'dark' | 'system'

function getSystemTheme(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function applyTheme(theme: Theme) {
  const resolved = theme === 'system' ? getSystemTheme() : theme
  document.documentElement.classList.toggle('dark', resolved === 'dark')
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    // ?_dark URL param wins over localStorage. Nursery appends it when
    // its App appearance setting is dark, and we want the iframe to
    // boot in that state rather than whatever this user's standalone
    // localStorage said last time.
    if (typeof window !== 'undefined' && new URLSearchParams(location.search).has('_dark')) {
      return 'dark'
    }
    const stored = localStorage.getItem('seeds-console-theme') as Theme | null
    return stored || 'system'
  })

  const setTheme = useCallback((t: Theme) => {
    localStorage.setItem('seeds-console-theme', t)
    setThemeState(t)
    applyTheme(t)
  }, [])

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  // Listen for system theme changes when in "system" mode
  useEffect(() => {
    if (theme !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => applyTheme('system')
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [theme])

  // Runtime theme override from a parent (Nursery's "App appearance"
  // setting). Routes through setTheme so the applyTheme effect above
  // doesn't clobber the parent's intent on the next render.
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.data?.type === 'set-dark-mode') {
        setTheme(e.data.dark ? 'dark' : 'light')
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [setTheme])

  const resolved = theme === 'system' ? getSystemTheme() : theme

  return { theme, resolved, setTheme }
}
